<script lang="ts">
import type { RssPreview } from '@codot/contracts';
import { mediaApi } from '../../api/content.api';
import { firstLocatedScreen } from '../../api/screens.api';

/** Shared across previews: one request per feed URL per page. */
const feeds = new Map<string, Promise<RssPreview | null>>();
function feedOf(url: string): Promise<RssPreview | null> {
  if (!feeds.has(url)) feeds.set(url, mediaApi.rssPreview(url).catch(() => null));
  return feeds.get(url)!;
}

/** "Player location" apps preview at a real player's place (the first with coordinates), else Dubai. */
interface Place {
  latitude: number;
  longitude: number;
  timezone: string;
}
const DUBAI: Place = { latitude: 25.2048, longitude: 55.2708, timezone: 'Asia/Dubai' };
let placeP: Promise<Place> | null = null;
function samplePlace(): Promise<Place> {
  placeP ??= firstLocatedScreen()
    .then((s) => (s ? { latitude: s.latitude!, longitude: s.longitude!, timezone: s.timezone } : DUBAI))
    .catch(() => DUBAI);
  return placeP;
}
</script>

<script setup lang="ts">
/**
 * An app inside a campaign/playlist preview section, laid out like the Android player's native widgets
 * (render/widget/InfoWidgets.kt + RssTickerWidget.kt): drawn at the section's real canvas size with the
 * player's size ratios (`fit(hf, wf)` = min(height×hf, width×wf)), then scaled down to the preview.
 * Time ticks; Prayer Time uses the player's algorithm; Weather shows sample conditions (no provider call);
 * RSS scrolls real headlines — only while the preview plays.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { WidgetConfig } from '@codot/contracts';
import { prayerTimes, type Prayer } from '../../utils/apps';
import { LEGACY_WIDGET_TYPES } from '@codot/contracts';
import MoreAppPreview from './MoreAppPreview.vue';
import { fontFamilyCss } from '../../utils/fonts';

const props = defineProps<{ config: WidgetConfig; playing: boolean; width: number; height: number }>();
const W = computed(() => props.width);
const H = computed(() => props.height);
const fit = (hf: number, wf: number) => `${Math.min(H.value * hf, W.value * wf)}px`;

/* ---- the canvas-size stage, scaled to the preview box ---- */
const box = ref<HTMLElement | null>(null);
const scale = ref(0);
let ro: ResizeObserver | undefined;
onMounted(() => {
  const measure = () => box.value && (scale.value = box.value.clientWidth / W.value);
  measure();
  ro = new ResizeObserver(measure);
  if (box.value) ro.observe(box.value);
});
onBeforeUnmount(() => ro?.disconnect());
watch(W, () => box.value && (scale.value = box.value.clientWidth / W.value));

/* ---- look (InfoWidgets.Look) ---- */
const text = computed(() => ('foreground' in props.config ? props.config.foreground : null) ?? '#FFFFFF');
/** The app's custom font, drawn like the player draws it (default: the CMS font). */
const font = computed(() => fontFamilyCss('fontId' in props.config ? props.config.fontId : null));
/** Phase 17.11 apps are drawn by MoreAppPreview (sized by container units). */
const isMore = computed(() => !LEGACY_WIDGET_TYPES.includes(props.config.type));
const panel = computed(() => ('background' in props.config ? props.config.background : null) ?? 'rgba(0,0,0,0.7)');

/* ---- shared ---- */
const now = ref(Date.now());
/**
 * The clock ticks every second only while the preview plays; a still preview (library tiles, a paused preview)
 * moves on each half minute and skips work while off screen or in a hidden tab. Dozens of tiles redrawing every
 * second made the editor sluggish and the preview flicker.
 */
let clockTimer: ReturnType<typeof setInterval> | undefined;
let visible = true;
let io: IntersectionObserver | undefined;
const tickNow = () => visible && !document.hidden && (now.value = Date.now());
watch(
  () => props.playing,
  (p) => {
    clearInterval(clockTimer);
    clockTimer = setInterval(tickNow, p ? 1000 : 30_000);
    now.value = Date.now();
  },
  { immediate: true },
);
onMounted(() => {
  if (!box.value || typeof IntersectionObserver === 'undefined') return;
  io = new IntersectionObserver(([e]) => {
    visible = !!e?.isIntersecting;
    if (visible) now.value = Date.now();
  });
  io.observe(box.value);
});
onBeforeUnmount(() => {
  clearInterval(clockTimer);
  io?.disconnect();
});
const place = ref<Place>(DUBAI);
void samplePlace().then((p) => (place.value = p));
const lang = computed(() => ('language' in props.config ? props.config.language : 'en'));
const arabic = computed(() => lang.value === 'ar');
const locale = computed(() => (lang.value === 'ar' ? 'ar' : lang.value === 'ur' ? 'ur' : 'en-GB'));
const nums = computed(() => (arabic.value ? 'arab' : 'latn'));
const zone = computed(() => (props.config.type === 'time' && props.config.timezone) || place.value.timezone);
const fmt = (ms: number, o: Intl.DateTimeFormatOptions, loc = locale.value) =>
  new Intl.DateTimeFormat(loc, { timeZone: zone.value, numberingSystem: nums.value, ...o }).format(ms);

/* ---- Time (TimeWidgetView + ClockFace) ---- */
const time = computed(() => {
  if (props.config.type !== 'time') return null;
  const c = props.config;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: zone.value, hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' })
      .formatToParts(now.value)
      .map((p) => [p.type, Number(p.value)]),
  ) as Record<string, number>;
  const minutes = parts.minute! + (c.showSeconds ? parts.second! / 60 : 0);
  const round = c.style === 'round';
  const chars = c.showSeconds ? 8 : 5;
  return {
    round,
    seconds: c.showSeconds,
    showDate: c.showDate,
    showLocation: c.showLocation,
    showTime: c.showTime,
    text: fmt(now.value, { hour: c.format === '12h' ? 'numeric' : '2-digit', minute: '2-digit', second: c.showSeconds ? '2-digit' : undefined, hour12: c.format === '12h' }),
    date: fmt(now.value, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    city: zone.value.split('/').pop()!.replace(/_/g, ' '),
    h: (((parts.hour! % 12) + minutes / 60) / 12) * 360,
    m: (minutes / 60) * 360,
    s: (parts.second! / 60) * 360,
    face: Math.min(H.value * (c.showDate || c.showLocation || c.showTime ? 0.6 : 0.8), W.value * 0.8),
    timeSize: round ? fit(0.08, 0.06) : fit(c.showDate ? 0.45 : 0.6, 1.55 / (chars + 1)),
  };
});

/* ---- Prayer Time (PrayerTimeWidgetView) ---- */
const PRAYER_LABELS: Record<string, string[]> = {
  en: ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'],
  ar: ['الفجر', 'الشروق', 'الظهر', 'العصر', 'المغرب', 'العشاء'],
  ur: ['فجر', 'طلوعِ آفتاب', 'ظہر', 'عصر', 'مغرب', 'عشاء'],
};
const ORDER: Prayer[] = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
const prayer = computed(() => {
  if (props.config.type !== 'prayer_time') return null;
  const c = props.config;
  const loc = c.location.source === 'fixed' ? { latitude: c.location.latitude, longitude: c.location.longitude } : place.value;
  const [y, mo, d] = new Intl.DateTimeFormat('en-CA', { timeZone: zone.value }).format(now.value).split('-').map(Number);
  const t = prayerTimes({ year: y!, month: mo!, day: d! }, loc.latitude, loc.longitude, c.calculationMethod, c.asrMethod);
  const next = ORDER.filter((p) => p !== 'sunrise').find((p) => (t[p] ?? 0) > now.value) ?? 'fajr';
  return {
    horizontal: W.value > H.value * 2,
    rtl: c.language !== 'en',
    rows: ORDER.map((p, i) => ({
      key: p,
      label: PRAYER_LABELS[c.language]![i]!,
      // The player formats HH:mm in Locale.US, then swaps in Arabic-Indic digits for Arabic.
      time: t[p] == null ? '—' : fmt(t[p]!, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, 'en-GB'),
      hl: c.highlightNext && p === next,
    })),
  };
});

/* ---- Weather (sample conditions, like the Customize App preview) ---- */
const weather = computed(() => {
  if (props.config.type !== 'weather') return null;
  const c = props.config;
  const imperial = c.units === 'imperial';
  const unit = arabic.value ? (imperial ? '°ف' : '°م') : imperial ? '°F' : '°C';
  const conv = (cel: number) => Math.round(imperial ? cel * 1.8 + 32 : cel);
  const n = (v: number) => new Intl.NumberFormat(locale.value, { numberingSystem: nums.value }).format(v);
  const horizontal = W.value >= H.value;
  return {
    horizontal,
    temp: `${n(conv(34))}${unit}`,
    word: arabic.value ? 'صافٍ' : 'Clear',
    icon: Math.min(H.value * (horizontal ? 0.55 : 0.2), W.value * 0.3),
    dayIcon: horizontal ? Math.min(H.value * 0.36, W.value * 0.06) : Math.min(H.value * 0.08, W.value * 0.16),
    days: Array.from({ length: c.forecastDays }, (_, i) => ({
      name: fmt(now.value + (i + 1) * 86_400_000, { weekday: 'short' }),
      icon: ['☀️', '⛅', '☀️', '🌤️', '☀️'][i]!,
      range: `${n(conv(35 - i))}° ${n(conv(27 - (i % 2)))}°`,
    })),
  };
});

/* ---- RSS ticker (RssTickerWidget: font = height × ratio; speed = font heights per second) ---- */
const rss = computed(() => (props.config.type === 'rss' ? props.config : null));
const headlines = ref<string | null>(null);
watch(
  () => rss.value?.feedUrl,
  async (url) => {
    headlines.value = null;
    if (!url) return;
    const f = await feedOf(url);
    if (url !== rss.value?.feedUrl) return;
    headlines.value = f?.items.slice(0, rss.value.maxItems).map((i) => i.title).join('     •     ') || '';
  },
  { immediate: true },
);
const FONT = { small: 0.38, medium: 0.46, large: 0.55 } as const;
const SPEED = { slow: 1.6, medium: 2.5, fast: 4.0 } as const;
const tickerEl = ref<HTMLElement | null>(null);
const offset = ref(0);
let raf = 0;
let last = 0;
function frame(t: number): void {
  if (rss.value && tickerEl.value) {
    const fontPx = H.value * FONT[rss.value.fontSize];
    if (last && props.playing) offset.value += ((t - last) / 1000) * fontPx * SPEED[rss.value.speed];
    if (offset.value > W.value + tickerEl.value.scrollWidth) offset.value = 0;
  }
  last = t;
  raf = requestAnimationFrame(frame);
}
onMounted(() => (raf = requestAnimationFrame(frame)));
onBeforeUnmount(() => cancelAnimationFrame(raf));
</script>

<template>
  <MoreAppPreview v-if="isMore" :config="config" :live="playing" />
  <div v-else ref="box" dir="ltr" class="relative h-full w-full overflow-hidden">
    <div
      v-if="scale"
      class="absolute top-0 start-0 flex origin-top-left items-center justify-center overflow-hidden"
      :style="{ width: `${W}px`, height: `${H}px`, transform: `scale(${scale})`, background: panel, color: text, fontFamily: font }"
    >
      <!-- Time -->
      <div v-if="time" class="flex flex-col items-center" :dir="arabic ? 'rtl' : 'ltr'">
        <svg v-if="time.round" viewBox="0 0 100 100" :style="{ width: `${time.face}px`, height: `${time.face}px` }" aria-hidden="true">
          <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" stroke-width="5" />
          <line
            v-for="i in 12"
            :key="i"
            x1="50"
            :y1="50 - 50 * (i % 3 === 0 ? 0.74 : 0.82)"
            x2="50"
            :y2="50 - 50 * 0.88"
            stroke="currentColor"
            :stroke-width="i % 3 === 0 ? 5 : 3"
            stroke-linecap="round"
            :transform="`rotate(${i * 30} 50 50)`"
          />
          <line x1="50" y1="50" x2="50" y2="25" stroke="currentColor" stroke-width="8" stroke-linecap="round" :transform="`rotate(${time.h} 50 50)`" />
          <line x1="50" y1="50" x2="50" y2="14" stroke="currentColor" stroke-width="5" stroke-linecap="round" :transform="`rotate(${time.m} 50 50)`" />
          <line v-if="time.seconds" x1="50" y1="50" x2="50" y2="10" stroke="#FFC857" stroke-width="2" stroke-linecap="round" :transform="`rotate(${time.s} 50 50)`" />
          <circle cx="50" cy="50" r="5" fill="currentColor" />
        </svg>
        <span v-if="!time.round || time.showTime" class="leading-tight whitespace-nowrap tabular-nums" :class="time.round ? '' : 'font-medium'" :style="{ fontSize: time.timeSize }">{{ time.text }}</span>
        <span v-if="time.showDate" class="leading-tight whitespace-nowrap tabular-nums opacity-75" :style="{ fontSize: fit(0.1, 0.055) }">{{ time.date }}</span>
        <span v-if="time.showLocation" class="leading-tight whitespace-nowrap opacity-75" :style="{ fontSize: fit(0.07, 0.045) }">{{ time.city }}</span>
      </div>

      <!-- Prayer Time -->
      <div v-else-if="prayer" class="w-full" :dir="prayer.rtl ? 'rtl' : 'ltr'">
        <div v-if="prayer.horizontal" class="flex w-full items-center justify-evenly">
          <div
            v-for="r in prayer.rows"
            :key="r.key"
            class="flex flex-col items-center"
            :style="{ padding: `${H * 0.04}px ${H * 0.06}px`, borderRadius: `${Math.min(W, H) * 0.03}px`, background: r.hl ? '#FFC857' : undefined, color: r.hl ? '#000' : undefined }"
          >
            <span class="leading-tight whitespace-nowrap" :class="r.hl ? '' : 'opacity-75'" :style="{ fontSize: fit(0.2, 0.022) }">{{ r.label }}</span>
            <span class="leading-tight font-medium whitespace-nowrap tabular-nums" :style="{ fontSize: fit(0.34, 0.035) }">{{ r.time }}</span>
          </div>
        </div>
        <div v-else class="flex w-full flex-col" :style="{ padding: `0 ${W * 0.06}px`, gap: `${H * 0.012}px` }">
          <div
            v-for="r in prayer.rows"
            :key="r.key"
            class="flex items-center"
            :style="{
              padding: `${H * 0.012}px ${W * 0.04}px`,
              fontSize: fit(0.055, 0.075),
              borderRadius: `${Math.min(W, H) * 0.03}px`,
              background: r.hl ? '#FFC857' : undefined,
              color: r.hl ? '#000' : undefined,
            }"
          >
            <span class="flex-1 whitespace-nowrap" :class="r.hl ? '' : 'opacity-75'">{{ r.label }}</span>
            <span class="font-medium whitespace-nowrap tabular-nums">{{ r.time }}</span>
          </div>
        </div>
      </div>

      <!-- Weather -->
      <div v-else-if="weather" class="flex w-full items-center justify-evenly" :class="weather.horizontal ? '' : 'flex-col'" :dir="arabic ? 'rtl' : 'ltr'">
        <div class="flex items-center">
          <span :style="{ fontSize: `${weather.icon * 0.8}px`, lineHeight: 1 }" aria-hidden="true">☀️</span>
          <div :style="{ paddingInlineStart: `${weather.icon * 0.2}px` }">
            <p class="leading-tight font-medium tabular-nums" :style="{ fontSize: fit(weather.horizontal ? 0.36 : 0.12, 0.12) }">{{ weather.temp }}</p>
            <p class="leading-tight opacity-75" :style="{ fontSize: fit(weather.horizontal ? 0.13 : 0.045, 0.05) }">{{ weather.word }}</p>
          </div>
        </div>
        <div v-if="weather.days.length" class="flex" :style="{ gap: `${Math.min(W, H) * 0.05}px` }">
          <div v-for="d in weather.days" :key="d.name" class="flex flex-col items-center">
            <span class="opacity-75" :style="{ fontSize: weather.horizontal ? fit(0.18, 0.03) : fit(0.04, 0.055) }">{{ d.name }}</span>
            <span :style="{ fontSize: `${weather.dayIcon * 0.8}px`, lineHeight: 1 }" aria-hidden="true">{{ d.icon }}</span>
            <span class="tabular-nums" :style="{ fontSize: weather.horizontal ? fit(0.2, 0.032) : fit(0.045, 0.06) }">{{ d.range }}</span>
          </div>
        </div>
      </div>

      <!-- RSS -->
      <template v-else-if="rss">
        <p
          v-if="headlines"
          ref="tickerEl"
          class="absolute whitespace-nowrap"
          :style="{
            fontSize: `${H * FONT[rss.fontSize]}px`,
            lineHeight: 1.2,
            [rss.direction === 'rtl' ? 'right' : 'left']: '100%',
            transform: `translateX(${rss.direction === 'rtl' ? offset : -offset}px)`,
          }"
          :dir="rss.direction"
        >
          {{ headlines }}
        </p>
        <span v-else class="opacity-70" :style="{ fontSize: `${H * 0.3}px` }">{{ headlines === '' ? 'No headlines' : 'Loading headlines…' }}</span>
      </template>
    </div>
  </div>
</template>
